import { useMutation } from '@sequencehq/api'
import {
  Dashboardv99990101Api,
  dashboardv99990101Client
} from '@sequencehq/api/dashboard/v99990101'
import {
  CheckboxLineField,
  RadioButtonGroupField,
  TextAreaField,
  useNotifications,
  Modal,
  Box,
  Flex
} from '@sequencehq/core-components'
import {
  GreyGrey70,
  IndigoIndigo60,
  Primary13Regular,
  Primary13SemiBold
} from '@sequencehq/core-components'
import { useForm } from '@sequencehq/utils'
import { required } from '@sequencehq/utils/validation'
import { useState } from 'react'

type FeedbackValue =
  | Dashboardv99990101Api.PostContractIntakeFeedback.FeedbackValue
  | undefined
const LLMFeedbackModal = (props: {
  automationExecutionId: string
  onClose: () => void
}) => {
  const { displayNotification } = useNotifications()
  const submitFeedbackMutation = useMutation(
    dashboardv99990101Client.postContractIntakeFeedback
  )

  const form = useForm<{
    value: FeedbackValue | undefined
    comment: string
    error: boolean
  }>({
    value: {
      value: 'AMAZING',
      comment: '',
      error: false
    },
    fieldConfiguration: [
      {
        property: 'value',
        validation: [required]
      },
      {
        property: 'comment',
        validation: []
      },
      {
        property: 'error',
        validation: []
      }
    ]
  })

  return (
    <Modal
      isOpen
      onClose={props.onClose}
      data-testid="watchtower.llmFeedback.modal"
    >
      <Modal.Header
        showCloseButton
        data-testid="watchtower.llmFeedback.modal.header"
      >
        Contract intake feedback
      </Modal.Header>
      <Modal.Content>
        <Flex direction="column" gap="16px">
          <RadioButtonGroupField
            label="Extraction accuracy"
            onChange={value =>
              form.fields.value.onChange(value as FeedbackValue)
            }
            data-testid="watchtower.llmFeedback.modal.quality"
            options={[
              { value: 'BAD', label: 'Bad' },
              { value: 'NOT_GREAT', label: 'Not great' },
              { value: 'GOOD', label: 'Good' },
              { value: 'AMAZING', label: 'Amazing' }
            ]}
            value={form.fields.value.value}
            validationErrors={form.fields.value.validationErrors}
            fullWidth
          />
          <Box>
            <Box {...Primary13SemiBold} color={GreyGrey70} marginBottom="8px">
              Issues
            </Box>
            <CheckboxLineField
              text="I encountered an error"
              data-testid="watchtower.llmFeedback.modal.error"
              {...form.fields.error}
            />
          </Box>
          <TextAreaField
            label="Additional comments"
            data-testid="watchtower.llmFeedback.modal.comment"
            placeholder={
              form.fields.error.value
                ? 'Include details of the error, along with any other additional feedback or context'
                : 'Any other feedback'
            }
            {...form.fields.comment}
          />
        </Flex>
      </Modal.Content>
      <Modal.Footer>
        <Modal.CancelButton data-testid="watchtower.llmFeedback.modal.cancel">
          Cancel
        </Modal.CancelButton>
        <Modal.SubmitButton
          text="Submit feedback"
          submitting={submitFeedbackMutation.isPending}
          data-testid="watchtower.llmFeedback.modal.submitFeedback"
          submittingText="Submitting..."
          onClick={async () => {
            try {
              await submitFeedbackMutation.mutateAsync({
                workflowId: props.automationExecutionId,
                body: {
                  value: form.fields.value.value!,
                  comment: form.fields.comment.value,
                  error: form.fields.error.value
                }
              })
              displayNotification('Feedback submitted', {
                type: 'success'
              })
              props.onClose()
              return
            } catch {
              displayNotification('Feedback could not be submitted', {
                type: 'error'
              })
            }
          }}
        />
      </Modal.Footer>
    </Modal>
  )
}

export const LLMFeedback = ({
  automationExecutionId
}: {
  automationExecutionId: string
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)

  return (
    <>
      <Box
        {...Primary13Regular}
        display="inline-block"
        cursor="pointer"
        color={IndigoIndigo60}
        onClick={() => setIsModalOpen(true)}
        data-testid="watchtower.llmFeedback"
      >
        Give us feedback
      </Box>
      {isModalOpen && (
        <LLMFeedbackModal
          automationExecutionId={automationExecutionId}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </>
  )
}
